import { Card, CardContent, CardMedia, Box, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SubTitle, Paragraph, Caption } from '../Shared/TypographyComponent';
import { animated, useSpring } from '@react-spring/web';

/*
Blog Card Component
-------------------
Reusable card for displaying blog post previews
*/

const AnimatedCard = animated(Card);

function BlogCard({ post, featured = false }) {
  const navigate = useNavigate();
  const theme = useTheme();

  // Hover animation
  const [hoverProps, api] = useSpring(() => ({
    transform: 'scale(1) translateY(0px)',
    boxShadow: `0px 4px 8px ${theme.palette.primary.main}33`, // main color at 20% opacity
    config: { tension: 300, friction: 20 }
  }));

  const handleMouseEnter = () => {
    api.start({
      transform: 'scale(1.02) translateY(-4px)',
      boxShadow: `0px 8px 24px ${theme.palette.text.link}4D` // link color at 30% opacity
    });
  };

  const handleMouseLeave = () => {
    api.start({
      transform: 'scale(1) translateY(0px)',
      boxShadow: `0px 4px 8px ${theme.palette.primary.main}33` // main color at 20% opacity
    });
  };

  const handleClick = () => {
    navigate(`/blog/${post.slug}`);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <AnimatedCard
      style={hoverProps}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        borderRadius: '8px',
        backgroundColor: `${theme.palette.primary.main}0D`, // main color at 5% opacity
        border: `1px solid ${theme.palette.primary.main}1A`, // main color at 10% opacity
        height: featured ? 'auto' : '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease'
      }}
    >
      {/* Thumbnail Image */}
      <CardMedia
        component="img"
        image={post.thumbnail}
        alt={post.title}
        sx={{
          aspectRatio: '16/9',
          objectFit: 'cover'
        }}
      />

      {/* Card Content */}
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>

        {/* Date */}
        <Box sx={{ mb: 1.5 }}>
          <Caption sx={{ opacity: 0.6 }}>
            {formatDate(post.date)}
          </Caption>
        </Box>

        {/* Title */}
        <Box sx={{ mb: 1.5 }}>
          <SubTitle sx={{
            fontSize: featured ? { xs: '1.5rem', sm: '1.75rem' } : { xs: '1.25rem', sm: '1.5rem' },
            lineHeight: 1.3
          }}>
            {post.title}
          </SubTitle>
        </Box>

        {/* Excerpt */}
        <Box sx={{ flexGrow: 1 }}>
          <Paragraph sx={{
            opacity: 0.7,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical'
          }}>
            {post.excerpt}
          </Paragraph>
        </Box>
      </CardContent>
    </AnimatedCard>
  );
}

export default BlogCard;
